import { ChevronRight, Menu as MenuIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate } from '@tanstack/react-router';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../icons';

import { useNovel } from '@/context/novel-context';
import { useUI } from '@/context/ui-context';
import { SidebarNavigation } from './sidebar-navigation';
import { SidebarProjectSelector } from './sidebar-project-selector';
import { TopHeader } from './top-header';
import { DashboardTour } from '../tour/dashboard-tour';

const DRAWER_WIDTH = 210;
const COLLAPSED_DRAWER_WIDTH = 70;

export function AppShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { selectedNovelId, setSelectedNovelId } = useNovel();
  const isEditorOrPublish = pathname.startsWith('/editor') || pathname.startsWith('/publish-editor');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useTranslation();
  const { isFocusMode } = useUI();

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleLogoClick = () => {
    if (selectedNovelId) {
      setSelectedNovelId(null);
    }
    navigate({ to: '/' });
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const currentDrawerWidth = isFocusMode ? 0 : (collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />

      {/* Sidebar - Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, borderRight: '1px solid', borderColor: 'divider' },
        }}
      >
        <Box
          onClick={handleLogoClick}
          sx={{
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            minHeight: 64,
            gap: 1.5,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: 1,
            }}
          >
            <Icons.logo width={36} height={36} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>{t('marketing.title')}</Typography>
        </Box>
        <Divider />
        <SidebarNavigation onNavItemClick={() => setMobileOpen(false)} collapsed={false} />
        <Box sx={{ mt: 'auto' }}>
          <SidebarProjectSelector collapsed={false} />
        </Box>
      </Drawer>

      {/* Sidebar - Desktop */}
      {!isFocusMode && (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            flexShrink: 0,
            width: currentDrawerWidth,
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          }}
          open
        >
          <Box sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            minHeight: 64,
            position: 'relative',
            px: collapsed ? 0 : 2,
            justifyContent: collapsed ? 'center' : 'flex-start'
          }}>
            {!collapsed && (
              <Box
                onClick={handleLogoClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexGrow: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 48,
                    height: 48,
                    borderRadius: 1,
                    flexShrink: 0
                  }}
                >
                  <Icons.logo width={36} height={36} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
                  {t('marketing.title')}
                </Typography>
              </Box>
            )}

            <IconButton
              onClick={handleToggleCollapse}
              size="small"
              sx={{
                position: collapsed ? 'static' : 'absolute',
                right: 8,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                zIndex: 1,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              {collapsed ? <ChevronRight sx={{ fontSize: 16 }} /> : <MenuIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Box>

          <Divider sx={{ opacity: 0.5 }} />

          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            <SidebarNavigation collapsed={collapsed} />
          </Box>
          <Box>
            <SidebarProjectSelector collapsed={collapsed} />
          </Box>
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${currentDrawerWidth}px)` },
          height: isEditorOrPublish ? '100vh' : 'auto',
          minHeight: isEditorOrPublish ? 'none' : '100vh',
          overflow: isEditorOrPublish ? 'hidden' : 'visible',
          display: 'flex',
          flexDirection: 'column',
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <TopHeader
          onMobileMenuToggle={() => setMobileOpen(!mobileOpen)}
          onLogoClick={handleLogoClick}
          showMobileMenu={true}
          showMobileBrand={!mobileOpen}
          showDesktopBrand={collapsed || isFocusMode}
        />
        <Container
          maxWidth={isEditorOrPublish ? false : "xl"}
          sx={{
            py: isEditorOrPublish ? 0 : { xs: 3, md: 4 },
            px: isEditorOrPublish ? 0 : undefined,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            height: isEditorOrPublish ? { xs: 'calc(100vh - 57px)', sm: 'calc(100vh - 65px)' } : 'auto'
          }}
          disableGutters={isEditorOrPublish}
        >
          {children}
        </Container>
        <DashboardTour />
      </Box>
    </Box>
  );
}
